import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { manualProspectSchema } from '../../../schema/prospect_schema';
import { uploadManualProspectThunk } from '../../../redux/thunks/prospect';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useRef, useState } from 'react';

const combinedHeaders = [
    'Company name',
    'Email',
    'Phone Number',
    'Address 1',
    'Address 2',
    'City',
    'Zip',
    'State',
    'Country',
    'Category',
    'Website',
    'Facebook',
    'Facebook Pixel (yes/no)',
    'Google Analytics (yes/no)',
    'Google Remarketing (yes/no)',
    'Instagram',
    'LinkedIn',
    'LinkedIn Analytics (yes/no)',
    'Mobile Friendly (yes/no)',
    'Shopify (yes/no)',
    'SSL (yes/no)',
    'SEO Schema (yes/no)',
    'Twitter',
    'WordPress (yes/no)'
];
/**
 * @heading1: UploadProspectsModal
 *
 * @paragraph: A modal component for uploading prospects or downloading a template for prospect data.
 * Users can either upload a CSV file or download a CSV template for importing prospects.
 *
 * @param: {boolean} open - Determines whether the modal is open.
 * @param: {function} onClose - Function to handle closing the modal.
 */

const UploadProspectsModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * loaders: An object containing the loader details from the Redux store.
     */
    const { loaders } = useSelector((state: RootState) => state.prospect);
    const [showInputs, setShowInputs] = useState<boolean>(false);
    const dispatch = useDispatch<AppDispatch>();
    const [base64Data, setBase64Data] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [error, setError] = useState<string | null>(null);
    const initialValues: {
        title: string;
        city: string;
        state: string;
        country: string;
    } = {
        city: '',
        title: '',
        state: '',
        country: ''
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file) {
            const allowedTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
            if (!allowedTypes.includes(file.type)) {
                setError('Only CSV or Excel files are allowed.');
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                setBase64Data(result);
                setError(null);
                setShowInputs(true);
            };
            reader.onerror = () => {
                setError('Error reading the file.');
            };

            reader.readAsDataURL(file);
        }
    };

    /**
     * Triggers the file input click when upload button is clicked.
     */
    const triggerFileSelect = () => {
        fileInputRef.current?.click();
    };

    const handleSubmit = async (values: {
        title: string;
        city: string;
        state: string;
        country: string;
    }) => {
        if (!base64Data) {
            setError('No file data available to upload.');
            return;
        }
        try {
            await dispatch(uploadManualProspectThunk({
                csv_file: base64Data,
                niche: values.title,
                city: values.city,
                state: values.state,
                country: values.country
            }));
            onClose();
        } catch (e) {
            //
        }
    };

    const downloadHandler = () => {
        const headerRow = `${combinedHeaders.join(',')}\n`;
        const blob = new Blob([headerRow], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'import_prospects_template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return <CustomModal open={open} handleClose={loaders.isCreating ? () => null : onClose} title="Upload prospects">
        {showInputs
            ? <Formik
                initialValues={initialValues}
                validationSchema={manualProspectSchema}
                onSubmit={handleSubmit}
            >
                {({ values, handleChange, handleBlur, errors, touched }) => (
                    <Form>
                        <Box display="flex" flexDirection="column" gap={2}>
                            {/* Input field for pipeline name */}
                            <CustomInput
                                value={values.title}
                                onChange={handleChange('title')}
                                onBlur={handleBlur('title')}
                                error={touched.title && errors.title}
                                fullWidth
                                label="Niche"
                                placeholder="Type here"
                                variant="outlined"
                                mandatoryFields={true}
                            />
                            {/* Input field for default deal value */}
                            <CustomInput
                                value={values.city}
                                onChange={handleChange('city')}
                                onBlur={handleBlur('city')}
                                error={touched.city && errors.city}
                                fullWidth
                                label="City"
                                placeholder="Type here"
                                variant="outlined"
                                mandatoryFields={true}
                            />
                            <CustomInput
                                value={values.state}
                                onChange={handleChange('state')}
                                onBlur={handleBlur('state')}
                                error={touched.state && errors.state}
                                fullWidth
                                label="State"
                                placeholder="Type here"
                                variant="outlined"
                                mandatoryFields={true}
                            />
                            <CustomInput
                                value={values.country}
                                onChange={handleChange('country')}
                                onBlur={handleBlur('country')}
                                error={touched.country && errors.country}
                                fullWidth
                                label="Country"
                                placeholder="Type here"
                                variant="outlined"
                                mandatoryFields={true}
                            />
                            {/* Action buttons for form submission or cancellation */}
                            <Box display="flex" gap={2} mt={2}>
                                <Button type="submit" variant="contained" color="primary" fullWidth>
                                    Continue
                                </Button>
                            </Box>
                        </Box>
                    </Form>
                )}
            </Formik>
            : <>
                {/* Informational text about uploading or downloading prospects */}
                <Typography variant="subtitle1" align="center" color="rgba(51,56,63,1)" fontSize="16px" fontWeight={500}>Upload you prospects here, or download the CSV file Import Prospects.</Typography>
                {/* Hidden File Input Field */}
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    accept=".csv, .xlsx, .xls"
                />
                {/* Action buttons for Cancel, Download Template, and Upload */}
                <Box display="flex" gap={2} mt={2}>
                    <Box width="100%"><Button variant="outlined" color="primary" onClick={onClose} fullWidth disabled={loaders.isCreating}>
                        Cancel
                    </Button></Box>
                    <Box width="100%"><Button type="submit" variant="contained" color="primary" onClick={downloadHandler} fullWidth disabled={loaders.isCreating}>
                        Download Template
                    </Button></Box>
                    <Box width="100%"><Button type="submit" variant="contained" color="primary" onClick={triggerFileSelect} fullWidth disabled={loaders.isCreating}>
                        {loaders.isCreating ? 'Uploading...' : 'Upload'}
                    </Button></Box>
                </Box>
                {/* Display error message if wrong file type is selected */}
                {error && <Typography color="error" fontSize="14px" textAlign="center" mt={1}>{error}</Typography>}
            </>}
    </CustomModal>;
};

export default UploadProspectsModal;